import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CategoryBadge from './CategoryBadge';
import { optimizeImage, getItemSlug } from '../lib/cms';

export default function ArticleCard({
  article,
  variant = 'grid',
  className = '',
}) {
  const navigate = useNavigate();
  if (!article) return null;

  const slug = getItemSlug(article);
  const articleUrl = `/artikler/${slug}`;
  const title = article.title || 'Uden titel';
  const subtitle = article.subtitle || '';
  const intro = article.intro || '';
  const coverUrl = article.cover_image?.[0] || '';
  const publishDate = article.publish_date || '';
  const author = article.author || 'Redaktionen';
  const readingTime = article.reading_time || '';
  const audience = article.audience || '';
  const isDraft = article.status === 'draft';
  const categoryName = article.terms?.category?.[0]
    ? article.terms.category[0].replace(/-/g, ' ')
    : '';

  const handleCardClick = (e) => {
    // If the click was on an inner link or button, let that element handle it
    if (e.target.closest('a, button, select, input')) {
      return;
    }
    navigate(articleUrl);
  };

  // Format date in Danish
  let formattedDate = publishDate;
  if (publishDate) {
    try {
      const d = new Date(publishDate);
      formattedDate = d.toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      formattedDate = publishDate;
    }
  }

  // Grand Lead Story (Frontpage Hero Article)
  if (variant === 'lead') {
    return (
      <article
        onClick={handleCardClick}
        className={`group relative flex flex-col gap-6 pb-8 border-b border-slate-200 cursor-pointer ${className}`}
      >
        {coverUrl && (
          <div className="w-full">
            <Link
              to={articleUrl}
              className="block overflow-hidden rounded-md border border-slate-200 bg-slate-50 shadow-2xs"
              aria-label={`Læs artikel: ${title}`}
            >
              <img
                src={optimizeImage(coverUrl, { width: 1400 })}
                alt={title}
                className="aspect-16/9 sm:aspect-21/9 max-h-[460px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </Link>
          </div>
        )}

        <div className="flex flex-col justify-between flex-1">
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <span className="inline-block px-2.5 py-0.5 text-[10px] font-bold tracking-widest uppercase bg-[#fd7302] text-white rounded-xs">
                Tophistorie
              </span>
              <CategoryBadge audience={audience} category={categoryName} size="xs" />
              {isDraft && (
                <span className="rounded-xs border border-amber-600 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                  Udkast
                </span>
              )}
            </div>

            <Link
              to={articleUrl}
              className="block group/title focus:outline-none"
            >
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-black text-[#010b27] leading-tight transition-colors group-hover/title:text-[#fd7302]">
                {title}
              </h2>
            </Link>

            {subtitle && (
              <p className="mt-2.5 font-serif text-base sm:text-lg italic text-slate-700 leading-snug">
                {subtitle}
              </p>
            )}

            {intro && (
              <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed line-clamp-3">
                {intro}
              </p>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#010b27]">Af {author}</span>
              <span>·</span>
              <time dateTime={publishDate}>{formattedDate}</time>
            </div>
            {readingTime && (
              <span className="font-mono text-[11px] text-[#fd7302] font-semibold">
                {readingTime}
              </span>
            )}
          </div>
        </div>
      </article>
    );
  }

  // Secondary Story
  if (variant === 'secondary') {
    return (
      <article
        onClick={handleCardClick}
        className={`group flex flex-col justify-between h-full pb-5 border-b border-slate-200 cursor-pointer ${className}`}
      >
        <div>
          {coverUrl && (
            <Link
              to={articleUrl}
              className="block mb-3 overflow-hidden rounded-md border border-slate-200 bg-slate-50 shadow-2xs"
              aria-label={`Læs artikel: ${title}`}
            >
              <img
                src={optimizeImage(coverUrl, { width: 600 })}
                alt={title}
                className="aspect-16/10 w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                loading="lazy"
              />
            </Link>
          )}

          <div className="mb-2">
            <CategoryBadge audience={audience} category={categoryName} size="xs" />
          </div>

          <Link
            to={articleUrl}
            className="block group/link focus:outline-none"
          >
            <h3 className="font-serif text-lg sm:text-xl font-bold text-[#010b27] leading-snug transition-colors group-hover/link:text-[#fd7302]">
              {title}
            </h3>
          </Link>

          {intro && (
            <p className="mt-2 text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-2">
              {intro}
            </p>
          )}
        </div>

        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
          <span>{author}</span>
          <time dateTime={publishDate}>{formattedDate}</time>
        </div>
      </article>
    );
  }

  // Compact Story (Sidebar list or ticker list)
  if (variant === 'compact') {
    return (
      <article
        onClick={handleCardClick}
        className={`group py-3.5 border-b border-slate-100 last:border-0 cursor-pointer ${className}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <CategoryBadge audience={audience} size="xs" />
          <time dateTime={publishDate} className="text-[10px] font-mono text-slate-500">
            {formattedDate}
          </time>
        </div>
        <Link
          to={articleUrl}
          className="block group/link focus:outline-none"
        >
          <h4 className="font-serif text-base font-bold text-[#010b27] leading-snug transition-colors group-hover/link:text-[#fd7302]">
            {title}
          </h4>
        </Link>
        {subtitle && (
          <p className="mt-1 text-xs text-slate-600 line-clamp-2 leading-relaxed">
            {subtitle}
          </p>
        )}
      </article>
    );
  }

  // Standard Grid Card (Used in Archive and Section listings)
  return (
    <article
      onClick={handleCardClick}
      className={`group flex flex-col justify-between rounded-md border border-slate-200 bg-white p-4 sm:p-5 shadow-xs transition-all duration-200 hover:border-[#fd7302]/50 hover:shadow-sm cursor-pointer ${className}`}
    >
      <div>
        {coverUrl && (
          <Link
            to={articleUrl}
            className="block mb-3.5 overflow-hidden rounded-sm border border-slate-100 bg-slate-50"
            aria-label={`Læs artikel: ${title}`}
          >
            <img
              src={optimizeImage(coverUrl, { width: 600 })}
              alt={title}
              className="aspect-16/10 w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </Link>
        )}

        <div className="flex items-center justify-between gap-2 mb-2">
          <CategoryBadge audience={audience} category={categoryName} size="xs" />
          {isDraft && (
            <span className="rounded-xs border border-amber-600 bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 uppercase">
              Udkast
            </span>
          )}
        </div>

        <Link
          to={articleUrl}
          className="block group/title focus:outline-none"
        >
          <h3 className="font-serif text-lg sm:text-xl font-bold text-[#010b27] leading-snug transition-colors group-hover/title:text-[#fd7302]">
            {title}
          </h3>
        </Link>

        {subtitle && (
          <p className="mt-1.5 font-serif text-xs sm:text-sm italic text-slate-600">
            {subtitle}
          </p>
        )}

        {intro && (
          <p className="mt-2.5 text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-3">
            {intro}
          </p>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-[#010b27]">{author}</span>
          <span>·</span>
          <time dateTime={publishDate}>{formattedDate}</time>
        </div>
        <Link
          to={articleUrl}
          className="font-semibold text-[#fd7302] hover:text-[#e06500] text-xs inline-flex items-center gap-1"
        >
          <span>Læs</span>
          <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}
